import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatDistanceToNow } from 'date-fns';

/**
 * Small colored status dot for an allocation's worker-acknowledgment state.
 *
 *   ⚫ draft             — dispatcher still drafting, worker can't see
 *   🟡 sent              — worker notified, awaiting acknowledgment
 *   🟢 confirmed         — worker tapped Confirm Receipt
 *   🔶 requires_re_ack   — allocation edited after ack; worker must re-confirm
 *
 * Props: { allocation, size = 8 }
 */
const statusMeta = (alloc) => {
  const s = alloc?.status || 'draft';
  if (alloc?.requires_re_ack) {
    return { cls: 'bg-amber-500 ring-amber-300/40', label: 'Re-ack required' };
  }
  if (s === 'confirmed') return { cls: 'bg-emerald-500 ring-emerald-300/40', label: 'Confirmed by worker' };
  if (s === 'sent') return { cls: 'bg-yellow-400 ring-yellow-200/40', label: 'Sent — awaiting ack' };
  return { cls: 'bg-slate-500 ring-slate-300/30', label: 'Draft — not sent' };
};

const relative = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : null);

export const WorkerAckDot = ({ allocation, size = 8 }) => {
  if (!allocation) return null;
  const meta = statusMeta(allocation);
  const tooltipLines = [meta.label];
  if (allocation.worker_ack_at) tooltipLines.push(`Confirmed ${relative(allocation.worker_ack_at)}`);
  else if (allocation.last_sent_at) tooltipLines.push(`Sent ${relative(allocation.last_sent_at)}`);
  if (allocation.worker_ack_notes) tooltipLines.push(`"${allocation.worker_ack_notes}"`);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-block rounded-full ring-2 ${meta.cls}`}
            style={{ width: size, height: size }}
            data-testid={`worker-ack-dot-${allocation.id}`}
            aria-label={meta.label}
          />
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          {tooltipLines.map((l, i) => <div key={i}>{l}</div>)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default WorkerAckDot;
